import { useState } from "react";
import { frontComments, hotRank, reactionTypes, type CommentItem } from "@/mocks/gossip";

const avatarTone = [
  "bg-primary-500",
  "bg-accent-500",
  "bg-secondary-700",
  "bg-primary-400",
  "bg-accent-600",
];

function CommentPanel() {
  const [items, setItems] = useState<CommentItem[]>(frontComments);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [reactionSel, setReactionSel] = useState<string[]>([]);
  const nextFloor = items.length + 1;

  const submit = () => {
    const v = text.trim();
    if (!v) {
      setError("先敲一句话再抢前排呀");
      return;
    }
    if (v.length > 120) {
      setError("别刷屏，一条最多 120 字");
      return;
    }
    setError("");
    const now: CommentItem = {
      id: Date.now(),
      user: "刚进瓜田的 91",
      time: "刚刚",
      text: v,
      floor: nextFloor,
      replies: 0,
      votes: 0,
    };
    setItems((prev) => [now, ...prev]);
    setText("");
  };

  const toggleReaction = (key: string) =>
    setReactionSel((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  return (
    <div id="comment" className="flex scroll-mt-20 flex-col gap-4 rounded-xl border border-background-200 bg-background-100 p-4 md:p-5">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-lg font-extrabold text-foreground-950">
          评论区前排占位 <i className="ri-chat-smile-3-fill text-primary-500" />
        </h4>
        <span className="num rounded-full bg-primary-100 px-2.5 py-1 text-xs font-bold text-primary-700">
          {items.length} 条热评
        </span>
      </div>
      <p className="text-xs text-foreground-700">手快才能抢到楼，第一线评论氛围就靠你们顶起来。</p>

      <div className="rounded-lg border border-background-200 bg-background-50 p-3">
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setError("");
          }}
          maxLength={120}
          rows={2}
          placeholder="比手速抢个前排，说说你吃到几成…"
          className="w-full resize-none bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
        />
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="num text-[11px] text-foreground-600">{text.length}/120</span>
          <button
            onClick={submit}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2 text-xs font-bold text-background-50 transition hover:bg-primary-600"
          >
            <i className="ri-run-line text-sm" /> 抢下第 {nextFloor} 楼
          </button>
        </div>
        {error && <p className="mt-2 text-xs text-primary-600">{error}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-foreground-700">这口瓜你：</span>
        {reactionTypes.map((r) => {
          const on = reactionSel.includes(r.key);
          const cnt = 4 + (on ? 1 : 0);
          return (
            <button
              key={r.key}
              onClick={() => toggleReaction(r.key)}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                on
                  ? "border-primary-400 bg-primary-100 text-primary-700"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
              }`}
            >
              <i className={`${r.icon} ${r.color}`} />
              {r.key}
              <span className="num text-foreground-600">{cnt}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-2.5">
        {items.map((c, i) => (
          <article key={c.id} className="rounded-lg bg-background-50 p-3">
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-background-50 ${avatarTone[i % avatarTone.length]}`}
              >
                {c.user.slice(0, 1)}
              </span>
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-xs font-bold text-foreground-950">{c.user}</span>
                <span className="num text-[10px] text-foreground-500">{c.time}</span>
              </div>
              <span className="num ml-auto rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] font-bold text-secondary-900">
                第 {c.floor} 楼
              </span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-foreground-800">{c.text}</p>
            <div className="mt-2 flex items-center gap-3 text-[11px] text-foreground-600">
              <span className="inline-flex items-center gap-1">
                <i className="ri-thumb-up-line" /> {c.votes}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-chat-1-line" /> {c.replies} 回复
              </span>
              <span className="ml-auto text-foreground-500">回应 / 顶</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function HotPanel() {
  const iconFor = (d: "up" | "down" | "new") =>
    d === "up" ? "ri-arrow-up-line text-primary-600" : d === "down" ? "ri-arrow-down-line text-foreground-600" : "ri-sparkling-line text-accent-600";

  return (
    <div id="rank" className="flex scroll-mt-20 flex-col gap-4 rounded-xl border border-background-200 bg-background-100 p-4 md:p-5">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-lg font-extrabold text-foreground-950">
          <i className="ri-fire-fill text-primary-500" /> 今日热瓜榜
        </h4>
        <span className="inline-flex items-center gap-1 text-[11px] text-foreground-600">
          <i className="ri-refresh-line" /> 整点热乎度刷新
        </span>
      </div>
      <ol className="flex flex-col">
        {hotRank.map((h) => (
          <li
            key={h.id}
            className="flex cursor-pointer items-center gap-3 border-b border-background-100 py-2.5 last:border-0"
          >
            <span
              className={`num inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-black ${
                h.rank <= 3 ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-foreground-700"
              }`}
            >
              {h.rank}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="truncate text-sm font-bold text-foreground-900">{h.title}</p>
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-[10px] font-semibold text-foreground-600">{h.tag}</span>
                <div className="h-1.5 w-full min-w-0 overflow-hidden rounded-full bg-background-200">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-accent-400 to-primary-500"
                    style={{ width: `${h.heat}%` }}
                  />
                </div>
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-foreground-600">
              <i className={iconFor(h.delta)} />
              <span className="num font-bold">{h.heat}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function SideRail() {
  return (
    <div className="flex flex-col gap-5">
      <CommentPanel />
      <HotPanel />
    </div>
  );
}